import api from './api';

export type SearchResultType =
  | 'estate'
  | 'asset'
  | 'liability'
  | 'cashflow'
  | 'participant'
  | 'document';

export interface SearchResult {
  type: SearchResultType;
  /** uid/uuid the detail route is built from. */
  id: string;
  title: string;
  subtitle?: string;
  estateId?: string;
  estateName?: string;
}

export interface GlobalSearchResponse {
  query: string;
  /** Which estates the server actually searched. */
  scope: 'active' | 'all';
  results: SearchResult[];
}

/**
 * Cross-entity search for the top bar.
 *
 * Scope is decided server-side from the user's own preference, so the client
 * sends the active estate and lets the backend decide whether to honour it.
 */
export const globalSearch = async (
  query: string,
  estateId?: string,
): Promise<GlobalSearchResponse> => {
  const params = new URLSearchParams({ q: query });
  if (estateId) {
    params.set('estateId', estateId);
  }

  const response = await api.get<GlobalSearchResponse>(`/search?${params.toString()}`);
  return response.data;
};

/** Route a result points at. */
export const searchResultPath = (result: SearchResult): string => {
  switch (result.type) {
    case 'estate':
      return `/estate/${result.id}`;
    case 'asset':
      return `/assets/${result.id}`;
    case 'liability':
      return `/liabilities/${result.id}`;
    case 'cashflow':
      return `/cashflows/${result.id}`;
    case 'participant':
      return `/participants/${result.id}`;
    case 'document':
      return `/probate-docs/${result.id}`;
    default:
      return '/estates';
  }
};

export const SEARCH_GROUP_LABELS: Record<SearchResultType, string> = {
  estate: 'Estates',
  asset: 'Assets',
  liability: 'Liabilities',
  cashflow: 'Cashflows',
  participant: 'Participants',
  document: 'Probate docs',
};

/** Order the groups appear in the dropdown. */
export const SEARCH_GROUP_ORDER: SearchResultType[] = [
  'estate',
  'asset',
  'liability',
  'cashflow',
  'participant',
  'document',
];
